import { useCallback, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { dismissFramingTip, isFramingTipDismissed } from '../lib/framingTip';

// Shared photo-capture flow used by the client gallery upload AND the
// cut-timer before/after capture. Two stages:
//   1. File picker — opens the device camera via `capture="environment"`.
//   2. Crop — user pans/pinches to center the head inside a square frame
//      with a faint head-position guide ring. Outputs a square Blob (default
//      1024×1024 JPEG).
//
// The OS camera UI is opaque to us, so the centering / framing UX *has* to
// happen post-capture. Hand-rolling pan-and-zoom is a touch-gesture rabbit
// hole on iOS; react-easy-crop handles those edge cases for ~6 KB.

// Output dimensions for the cropped photo. 2048 (up from 1024) gives
// enough resolution to preserve fade-line / beard detail when the
// photo is later upscaled into the 1080×1920 share composite. Modern
// share targets (Instagram, TikTok, Reels) all accept and resize
// 2048px source comfortably. File size goes from ~200 KB → ~800 KB
// per photo — well within reason for the cut-timer + client gallery
// use cases.
const OUTPUT_SIZE = 2048;
const OUTPUT_TYPE = 'image/jpeg';
// Quality bump 0.92 → 0.94 pairs with the larger output: more pixels +
// slightly less aggressive JPEG quantization = noticeably sharper fades.
const OUTPUT_QUALITY = 0.94;

export type PhotoCropArea = { x: number; y: number; width: number; height: number };

/** Which half of the eventual transformation composite this photo will
 *  end up in. When set, the crop step ghosts the picture-frame chrome
 *  over the cropper so the barber sees exactly which parts of the photo
 *  will land behind banners / borders / mascot before they commit.
 *  Unset for non-transformation captures (e.g. client gallery photo),
 *  which get the plain head-position ring instead. */
export type ComposeRole = 'before' | 'after';

export function PhotoCaptureModal({
  open,
  title,
  composeRole,
  onCapture,
  onCaptureNative,
  onClose,
}: {
  open: boolean;
  /** Headline copy — e.g. "Snap the before" or "Add client photo". */
  title: string;
  /** Optional. See ComposeRole — when present, crop step shows the
   *  frame overlay matching this half of the composite. */
  composeRole?: ComposeRole;
  /** Called with a square JPEG blob once the user confirms the crop.
   *  This is the standard low-friction path — what every consumer (client
   *  gallery upload, etc.) uses today. */
  onCapture: (blob: Blob) => Promise<void> | void;
  /** Optional high-fidelity callback. When provided, ALSO called with
   *  the original uncompressed camera File/Blob + the user's crop area
   *  in native pixel space. Lets a caller render from the native
   *  source directly (single-pass JPEG encoding for share composites).
   *  Cut timer's before/after capture wires this up; the client gallery
   *  upload deliberately doesn't (smaller storage blob is the point). */
  onCaptureNative?: (source: Blob, crop: PhotoCropArea) => void;
  /** Cancel / dismiss without capturing. */
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  // Hold a reference to the ORIGINAL File the user picked. The
  // onCapture path needs only the cropped blob, but the
  // onCaptureNative path also passes this back so the consumer can
  // render from native resolution. Ref so it doesn't trigger
  // re-renders.
  const sourceFileRef = useRef<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Framing-tip dismissal — read once at mount so flipping the flag
  // mid-session doesn't pop the card back up. Per-device, persisted to
  // localStorage by the close button.
  const [tipDismissed, setTipDismissed] = useState(() => isFramingTipDismissed());

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  function reset() {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    sourceFileRef.current = null;
    setImageUrl(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    sourceFileRef.current = file;
    setImageUrl(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }

  async function handleConfirm() {
    if (!imageUrl || !croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await renderSquareCrop(imageUrl, croppedAreaPixels);
      if (!blob) {
        toast(t('photoCapture.cropError'), 'error');
        setBusy(false);
        return;
      }
      // Hand off the native source + crop area FIRST so the
      // consumer's store is updated before they react to onCapture's
      // resolve. The crop area is in source-image native pixels —
      // exactly what canvas drawImage expects.
      if (onCaptureNative && sourceFileRef.current) {
        onCaptureNative(sourceFileRef.current, {
          x: croppedAreaPixels.x,
          y: croppedAreaPixels.y,
          width: croppedAreaPixels.width,
          height: croppedAreaPixels.height,
        });
      }
      await onCapture(blob);
      reset();
    } catch (err) {
      toast(t('photoCapture.cropError'), 'error');
    } finally {
      setBusy(false);
    }
  }

  // First-stage view: prompt the user to launch the camera. The hidden
  // <input> is what actually triggers it; the visible button styles the
  // affordance. A "choose existing" tap-out below opens the photo library
  // instead (no `capture` attribute → OS shows library picker by default
  // alongside camera on iOS).
  if (!open) return null;
  return (
    <ModalShell open onClose={handleClose} variant="card" size="md" ariaLabel={title}>
      <div className="bg-surface rounded-2xl">
        <div className="px-5 pt-5 pb-2">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500 mb-1">
            {t('photoCapture.eyebrow')}
          </div>
          <h2 className="font-display text-2xl leading-tight">{title}</h2>
          <p className="text-[12px] text-ink-500 mt-1.5 leading-snug">
            {imageUrl
              ? composeRole
                ? t('photoCapture.cropHintFramed')
                : t('photoCapture.cropHint')
              : t('photoCapture.captureHint')}
          </p>
        </div>

        {imageUrl ? (
          <>
            {/* Crop stage — square aspect, head-position guide ring on top. */}
            <div className="relative bg-bg-alt mx-5 my-3 rounded-xl overflow-hidden" style={{ height: 360 }}>
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={false}
                cropShape="rect"
                objectFit="cover"
                style={{
                  containerStyle: { background: '#0c1530' },
                }}
              />
              {/* Composition guide overlay — non-interactive, sits on
                  top of the cropper so the barber can see exactly
                  where chrome will eat into their photo before
                  committing. Two modes:
                    - composeRole set: full picture-frame ghost, half-
                      aligned (top for 'before', bottom for 'after') so
                      the BEFORE/AFTER banner appears in the matching
                      part of the cropper view. The transparent middle
                      of the frame lets the barber see their photo.
                    - composeRole unset: legacy head-position ring for
                      generic captures (client gallery uploads).
                  Frame asset is the same one photoCompose / videoStitch
                  bake into the final share, so what the barber sees
                  here is what they get in the share. */}
              {composeRole ? (
                <div
                  aria-hidden
                  className="absolute inset-0 overflow-hidden pointer-events-none"
                >
                  {/* Frame chrome at 75% opacity — strong enough to read
                      as a clear constraint without fully obscuring the
                      photo underneath. Top-aligned for 'before' so the
                      BEFORE banner caps the cropper; bottom-aligned for
                      'after' so the AFTER banner + mascot sit at the
                      bottom of the cropper. */}
                  <img
                    src="/assets/frame-portrait.png"
                    alt=""
                    className="absolute left-0 w-full"
                    style={{
                      [composeRole === 'before' ? 'top' : 'bottom']: 0,
                      opacity: 0.75,
                    }}
                  />
                  {/* Edge-trim bands — top ~5.6% and bottom ~5.7% of the
                      barber's cropped square get cropped off entirely
                      when the square is cover-fitted into the portrait
                      composite's 1080×957 photo half. The dark stripes
                      with subtle "EDGE TRIM" labels signal "anything
                      here disappears in the share" so the head goes
                      in the middle ~89% safe zone. */}
                  <div className="absolute top-0 left-0 right-0 bg-black/55 text-white text-[8px] tracking-[0.18em] uppercase font-extrabold flex items-center justify-center" style={{ height: '5.6%' }}>
                    <span>edge trim</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[8px] tracking-[0.18em] uppercase font-extrabold flex items-center justify-center" style={{ height: '5.6%' }}>
                    <span>edge trim</span>
                  </div>
                </div>
              ) : (
                <div
                  aria-hidden
                  className="absolute inset-0 grid place-items-center pointer-events-none"
                >
                  <div className="w-1/2 aspect-square rounded-full border-2 border-gold-400/55 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)]" />
                </div>
              )}
            </div>

            {/* Zoom slider — touch pinch already works via the lib; this
                is a fallback for desktop and a secondary control on phones. */}
            <div className="px-5 pb-2 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wide font-bold text-ink-500">
                {t('photoCapture.zoom')}
              </span>
              <input
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-gold-500"
                aria-label={t('photoCapture.zoom')}
              />
            </div>

            <div className="p-4 flex gap-2">
              <button
                type="button"
                onClick={reset}
                className="h-11 px-4 rounded-lg border border-line bg-bg-alt text-ink-700 font-semibold"
              >
                {t('photoCapture.retake')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy || !croppedAreaPixels}
                className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-50"
              >
                {busy ? t('photoCapture.saving') : t('photoCapture.use')}
              </button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-3">
            {/* Composition heads-up — only for transformation captures
                (composeRole set). Shows a small frame preview + the
                specific banner the barber needs to avoid, so they know
                what to compose for BEFORE handing off to the OS camera
                (where we can't draw an overlay). The full overlay on
                the crop step is the actual fitting tool; this is the
                pre-flight reminder. */}
            {composeRole && !tipDismissed && (
              <div className="rounded-xl border border-line bg-bg-alt p-3 flex items-start gap-3 relative">
                <div className="relative w-14 shrink-0 overflow-hidden rounded-md bg-black/40" style={{ aspectRatio: '9 / 16' }}>
                  <img
                    src="/assets/frame-portrait.png"
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                  />
                </div>
                <div className="min-w-0 flex-1 pr-5">
                  <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500">
                    {t('photoCapture.composeEyebrow')}
                  </div>
                  <p className="text-[12px] leading-snug text-ink-700 mt-0.5">
                    {composeRole === 'before'
                      ? t('photoCapture.composeHintBefore')
                      : t('photoCapture.composeHintAfter')}
                  </p>
                </div>
                {/* Dismiss button — flips the per-device localStorage
                    flag so the tip never shows again on this phone.
                    Hit area is generous (28×28) so a busy thumb doesn't
                    fat-finger the camera button below. */}
                <button
                  type="button"
                  onClick={() => {
                    dismissFramingTip();
                    setTipDismissed(true);
                  }}
                  aria-label={t('photoCapture.composeDismissAria')}
                  className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full text-ink-500 hover:text-ink-900 hover:bg-black/5"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 rounded-2xl border-2 border-dashed border-line-strong bg-bg-alt text-ink-700 grid place-items-center hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
            >
              <div className="flex flex-col items-center gap-1.5">
                <CameraIcon className="w-8 h-8 text-navy-900 dark:text-gold-400" />
                <span className="text-sm font-bold">{t('photoCapture.openCamera')}</span>
                <span className="text-[11px] text-ink-500">{t('photoCapture.openCameraHint')}</span>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
            />
            <button
              type="button"
              onClick={handleClose}
              className="w-full h-11 rounded-lg border border-line bg-surface text-ink-700 font-semibold"
            >
              {t('photoCapture.cancel')}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// Renders the cropped region of the source image to an offscreen canvas at
// OUTPUT_SIZE × OUTPUT_SIZE and returns it as a JPEG blob. Square only.
async function renderSquareCrop(srcUrl: string, area: Area): Promise<Blob | null> {
  const img = await loadImage(srcUrl);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  );
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), OUTPUT_TYPE, OUTPUT_QUALITY);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
